import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
  Play,
  Copy,
  X,
  Code,
  Server,
  Database,
  Activity,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getData, postDataWithBody } from "../../../Services/Services";
import {
  getApiQueriesList,
  getInstanceUrl,
  getThemeURL,
} from "../../../Services/EndPoints";
import Table from "../../../Components/UiComponents/Table/Table";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import {
  Deleteicon,
  Editicon,
  Viewicon,
} from "../../../Components/IconComponents";
import Confirmpopup from "../Common/ConfirmPopup/Confirmpopup";
import { useTranslation } from "react-i18next"; // Add this import
import DMSHeader from "../DMSHeader/DMSHeader";

const SavedApiQueriesList = () => {
  const { t } = useTranslation(); // Add this line
  const [savedQueries, setSavedQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [executionResult, setExecutionResult] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [activeTab, setActiveTab] = useState("query");
  const [currentQuery, setCurrentQuery] = useState(null);
  const [instanceData, setInstanceData] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "createdOn",
    direction: "desc",
  });
  const navigate = useNavigate();
  const itemsPerPage = 10;

  const handleEditQuery = (query) => {
    navigate("../api-builder", { state: { queryToEdit: query } });
  };

  useEffect(() => {
    fetchSavedQueries();
    fetchInstance();
  }, []);

  const fetchSavedQueries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getData(getApiQueriesList);

      const parsedData = response.map((item) => ({
        ...item,
        queryBuilder:
          typeof item.queryBuilder === "string"
            ? JSON.parse(item.queryBuilder)
            : item.queryBuilder,
      }));

      setSavedQueries(parsedData);
    } catch (err) {
      console.error("Failed to fetch saved queries:", err);
      setError("Failed to load saved services");
      toast.error("Failed to load saved services");
    } finally {
      setLoading(false);
    }
  };

  const deleteQuery = async (queryId) => {
    try {
      setLoading(true);

      // Here you would call your delete API endpoint
      // await fetch(`http://localhost:7080/iccc-service/service-metadata/delete/${queryId}`, {
      //   method: 'DELETE'
      // });

      // For now, we'll just filter it out from the state
      const updatedQueries = savedQueries.filter((q) => q.id !== queryId);
      setSavedQueries(updatedQueries);
      toast.success("Query deleted successfully");
    } catch (err) {
      console.error("Failed to delete query:", err);
      setError("Failed to delete service");
      toast.error("Failed to delete service");
    } finally {
      setLoading(false);
    }
  };

  const fetchInstance = async () => {
    try {
      const userProfile = await getData(getThemeURL);

      if (
        !userProfile ||
        !Array.isArray(userProfile) ||
        userProfile.length === 0
      ) {
        console.error("No user profile data found");
        return;
      }

      const user = userProfile[0];
      const roleName = user.role_name;

      let payload;

      if (roleName === "SUPER ADMIN") {
        payload = {
          type: "DEFAULT",
          bank_id: null,
        };
      } else if (user.is_admin === true) {
        payload = {
          type: "HO",
          bank_id: user.bank_id,
        };
      } else {
        payload = {
          type: "U",
          bank_id: user.bank_id,
        };
      }

      const response = await postDataWithBody(getInstanceUrl, payload, {});
      setInstanceData(response);
    } catch (error) {
      console.error("Error fetching instance data:", error);
    }
  };

  const executeQuery = async (query) => {
    try {
      setIsExecuting(true);
      console.log("Query response", query);

      // Parse the queryBuilder if it's a string (or use directly if it's already an object)
      const queryObject =
        typeof query.queryBuilder === "string"
          ? JSON.parse(query.queryBuilder)
          : query.queryBuilder;

      // Fetch user profile to determine auth parameters
      const userProfile = await getData(getThemeURL);

      if (
        !userProfile ||
        !Array.isArray(userProfile) ||
        userProfile.length === 0
      ) {
        toast.error("Failed to fetch user profile", { position: "top-center" });
        return;
      }

      const user = userProfile[0];
      const roleName = user.role_name;
      const userBankId = user.bank_id;

      // Check if instance data is available
      if (!instanceData || !instanceData[0]) {
        toast.error("Instance data not available", { position: "top-center" });
        return;
      }

      // Prepare auth object based on user role with updated structure
      const authObject = {
        instanceUrl: "/query-builder-api/execute",
        apiType: "POST",
      };

      if (roleName === "SUPER ADMIN") {
        authObject.type = "SA";
        // For super admin, use bank_id from instanceData
        const instanceBankId = instanceData[0].bank_id;
        authObject.bank_id = instanceBankId ? [instanceBankId] : [];
      } else if (user.is_admin === true) {
        authObject.type = "HO";
        authObject.bank_id = userBankId ? [userBankId] : [];
      } else {
        authObject.type = "U";
        authObject.bank_id = userBankId ? [userBankId] : [];
      }

      // DEEP CLONE instead of shallow clone
      const executionQueryObject = JSON.parse(JSON.stringify(queryObject));

      // Prepare the final payload
      const payload = {
        auth: authObject,
        main: executionQueryObject,
      };

      console.log("Executing query with payload:", payload);

      // Call the API endpoint
      const response = await fetch("/nifi/fetchQuery-API", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      const data = await response.json();
      setExecutionResult(data);
      setActiveTab("response");
      toast.success("Query executed successfully");
    } catch (err) {
      console.error("Failed to execute query:", err);
      setError("Failed to execute query");
      toast.error("Failed to execute query");
    } finally {
      setIsExecuting(false);
    }
  };

  const openViewModal = (query) => {
    setCurrentQuery(query);
    setExecutionResult(null);
    setActiveTab("query");
    setShowViewModal(true);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date";
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-indexed
    const year = String(date.getFullYear()).slice(-2); // last 2 digits
    return `${day}/${month}/${year}`;
  };

  const copyToClipboard = async (text) => {
    try {
      // Modern approach - requires HTTPS
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
      } else {
        // Fallback for non-HTTPS environments
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          toast.success("Copied to clipboard");
        } catch (err) {
          console.error('Fallback: Could not copy text', err);
          toast.error("Failed to copy to clipboard");
        }

        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error("Failed to copy to clipboard");
    }
  };

  const deleteNiFiServiceMetadata = async (id) => {
    try {
      setIsExecuting(true);

      const response = await fetch(`/nifi/service-metadata-api/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      // Show success message
      toast.success("Service metadata deleted successfully.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      fetchSavedQueries();
    } catch (error) {
      console.error("Error deleting NiFi service metadata:", error);
      toast.error(`Failed to delete metadata: ${error.message}`);
    } finally {
      setIsExecuting(false);
    }
  };

  const getEntityIcon = (entity) => {
    if (entity?.includes("user")) return <Activity size={16} />;
    if (entity?.includes("atm")) return <Database size={16} />;
    if (entity?.includes("bank")) return <Database size={16} />;
    if (entity?.includes("resource")) return <Database size={16} />;
    if (entity?.includes("branch")) return <Database size={16} />;
    return <Database size={16} />;
  };

  // Sorting functionality
  const requestSort = useCallback(
    (key) => {
      let direction = "asc";
      let sortKey = key;

      if (sortConfig.key === key) {
        if (sortConfig.direction === "asc") {
          direction = "desc";
        } else if (sortConfig.direction === "desc") {
          // Third click - reset to initial sort (createdOn desc)
          direction = "desc";
          sortKey = "createdOn";
        }
      }

      setSortConfig({ key: sortKey, direction });
    },
    [sortConfig.key, sortConfig.direction]
  );

  const sortedAndFilteredQueries = React.useMemo(() => {
    // First filter the data
    let filteredData = savedQueries.filter((query) => {
      const matchesSearch =
        query.serviceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        query.serviceDescription
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const isNotSecondary =
        query.queryStage !== "secondary" &&
        query.queryStage !== "Secondary" &&
        query.queryStage !== "SECONDARY";

      return matchesSearch && isNotSecondary;
    });

    // Then sort the filtered data - always apply sorting
    if (sortConfig.key && sortConfig.direction) {
      // This will always be true now
      filteredData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle null/undefined values
        if (aValue == null) aValue = "";
        if (bValue == null) bValue = "";

        // Special handling for dates
        if (sortConfig.key === "createdOn") {
          aValue = new Date(aValue).getTime() || 0;
          bValue = new Date(bValue).getTime() || 0;
        } else {
          // Convert to string for consistent comparison
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  }, [savedQueries, sortConfig, searchTerm]);

  const columns = React.useMemo(
    () => [
      {
        Header: "S.No.",
        accessor: (row, index) => index + 1,
        disableSortBy: true,
        Cell: ({ row }) => {
          return <span>{row.index + 1}</span>;
        },
      },
      // {
      //   Header: "Service Name",
      //   accessor: "serviceName",
      //   disableSortBy: false,
      //   Cell: ({ row }) => (
      //     <div
      //       style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
      //     >
      //       {row.original.serviceName}
      //     </div>
      //   ),
      // },
      {
        Header: () => (
          <div
            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            onClick={() => requestSort("serviceName")}
          >
            Service Name
            {sortConfig.key === "serviceName" &&
            sortConfig.direction === "asc" ? (
              <ChevronUp size={14} />
            ) : sortConfig.key === "serviceName" &&
              sortConfig.direction === "desc" ? (
              <ChevronDown size={14} />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "4px",
                }}
              >
                <ChevronUp size={12} style={{ marginBottom: "-2px" }} />
                <ChevronDown size={12} style={{ marginTop: "-2px" }} />
              </div>
            )}
          </div>
        ),
        accessor: "serviceName",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            {row.original.serviceName}
          </div>
        ),
      },
      // {
      //   Header: "Query Stage",
      //   accessor: "queryStage",
      //   disableSortBy: true,
      //   Cell: ({ value }) => value || "NA",
      // },
      {
        Header: "Widget Category",
        accessor: "widgetType",
        disableSortBy: true,
        Cell: ({ value }) => value || "NA",
      },
      {
        Header: "Description",
        accessor: "serviceDescription",
        disableSortBy: true,
        Cell: ({ value }) => value || "NA",
      },
      {
        Header: "Created On",
        accessor: "createdOn",
        disableSortBy: true,
        Cell: ({ value }) => (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {/* <Calendar size={14} style={{ color: "#94a3b8" }} /> */}
            {formatDate(value)}
          </div>
        ),
      },
      {
        Header: "Actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const [isdelete, setIsdelete] = useState(false);

          const deletePopUp = () => {
            setIsdelete(!isdelete);
          };

          const deleteTableDataById = async (id) => {
            await deleteNiFiServiceMetadata(id);
            setIsdelete(false);
          };

          console.log("Row api service data", row.original);
          return (
            <div style={{ display: "flex", gap: "0.75rem" }}>
              {/* View Button */}
              <span
                onClick={() => openViewModal(row.original)}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                title="View Details"
              >
                <Viewicon size={14} className="cursor-pointer me-2" />
              </span>

              {/* Edit Button */}
              <span
                onClick={() => handleEditQuery(row.original)}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                title="Edit"
              >
                <Editicon size={14} className="cursor-pointer me-2" />
              </span>

              {/* Delete Button */}
              <span
                onClick={deletePopUp}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                title="Delete"
              >
                <Deleteicon size={14} className="cursor-pointer mr-1" />
              </span>

              {/* Delete Confirmation Popup */}
              {isdelete && (
                <Confirmpopup
                  className="DeltePopup"
                  show={isdelete}
                  title={t("deletepopuptext") || "Delete Confirmation"}
                  message={
                    t("deletepopupmessage") ||
                    "Are you sure you want to delete this service? This action cannot be undone."
                  }
                  handleToggle={deletePopUp}
                  row={row}
                  name={row.original.serviceName}
                  deleteRow={deleteTableDataById}
                  cancel={t("cancelbutton") || "Cancel"}
                  action={t("actionbutton") || "Delete"}
                />
              )}
            </div>
          );
        },
      },
    ],
    [isExecuting, t, sortConfig]
  );

  return (
    <>
      <DMSHeader
        title="API Services"
        style={{
          borderRadius: "8px",
          marginBottom: "20px",
        }}
      />
      <div
        className="atm-table-container"
        style={{ marginTop: "10px", borderRadius: "8px" }}
      >
        <div className="branch-main-container-forAtm">
          <div
            className="d-flex align-items-center gap-4"
            style={{
              justifyContent: "space-between",
              padding: "10px",
              height: "45px",
            }}
          >
            {/* Total Services Button */}
            <label
              style={{
                backgroundColor: "#1a7a73",
                color: "#FFFFFF",
                fontWeight: "Bold",
                fontSize: "12px",
                fontFamily: "Poppins",
                height: "29px",
                padding: "6px 6px",
                borderRadius: "4px",
                marginTop: "6px",
                marginLeft: "12px",
                width: "126px",
              }}
            >
              Total Services: {sortedAndFilteredQueries.length}
            </label>

            {/* Search Input */}
            <div
              className="d-flex align-items-center shadow-sm rounded-2"
              style={{
                maxWidth: "250px",
                marginLeft: 15,
                backgroundColor: "white",
                height: "29px",
                padding: "0 8px",
                marginRight: "13px",
              }}
            >
              <input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  height: "29px",
                  backgroundColor: "#FFFFFF",
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  padding: 0,
                  margin: 0,
                  flex: 1,
                  color: "#67717A",
                  fontSize: "14px",
                  fontFamily: "Poppins",
                }}
              />
              <Search
                style={{ height: "12px", width: "12px", color: "#1a7a73" }}
              />
            </div>
          </div>
        </div>

        <div
          className="datatable__container p-1"
          style={{
            height: "500px", // Fixed height instead of minHeight
            overflow: "auto", // Enable scrolling instead of visible
          }}
        >
          {loading && !savedQueries.length ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "300px",
                backgroundColor: "white",
                borderRadius: "1rem",
                marginTop: "16px",
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <Loader2
                  size={32}
                  style={{
                    animation: "spin 1s linear infinite",
                    color: "#1a7a73",
                    marginBottom: "1rem",
                  }}
                />
                <p style={{ color: "#64748b", fontSize: "0.875rem" }}>
                  Loading services...
                </p>
              </div>
            </div>
          ) : error ? (
            <div
              style={{
                padding: "1.5rem",
                backgroundColor: "#fee2e2",
                color: "#b91c1c",
                borderRadius: "0.75rem",
                marginBottom: "1.5rem",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                border: "1px solid #f87171",
              }}
            >
              <div
                style={{
                  padding: "0.25rem",
                  backgroundColor: "#dc2626",
                  borderRadius: "50%",
                  color: "white",
                }}
              >
                <X size={14} />
              </div>
              <span style={{ fontWeight: "500" }}>{error}</span>
            </div>
          ) : sortedAndFilteredQueries.length === 0 ? (
            <div
              style={{
                padding: "3rem",
                textAlign: "center",
                backgroundColor: "white",
                borderRadius: "1rem",
                marginTop: "16px",
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              }}
            >
              <Database
                size={48}
                style={{ color: "#cbd5e1", marginBottom: "1rem" }}
              />
              <h3
                style={{
                  color: "#475569",
                  fontSize: "1.125rem",
                  marginBottom: "0.5rem",
                }}
              >
                {searchTerm
                  ? "No matching services found"
                  : "No saved services yet"}
              </h3>
              <p style={{ color: "#64748b", fontSize: "0.875rem" }}>
                {searchTerm
                  ? "Try adjusting your search terms"
                  : "Create your first database service to get started"}
              </p>
            </div>
          ) : (
            <Table
              data={sortedAndFilteredQueries}
              title=""
              showFilter={false}
              columns={columns}
              numberOfRows={itemsPerPage}
              showExport={false}
              showReset={false}
              onExpand={false}
              showSearch={false}
              isHeaderFixed={true}
              rowCount={sortedAndFilteredQueries.length}
              headerTableHeadStyle={{ background: "#1a7a73", color: "#FFFFFF" }}
              tableStyle={{
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                marginTop: "-15px",
                padding: "0px",
              }}
            />
          )}
        </div>

        {/* View Modal */}
        <Modal
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{currentQuery?.serviceName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <p style={{ color: "#64748b", fontSize: "0.875rem" }}>
                {currentQuery?.serviceDescription}
              </p>
            </div>

            <div className="d-flex border-bottom mb-3">
              <button
                className={`py-2 px-3 d-flex align-items-center ${
                  activeTab === "query"
                    ? "border-bottom border-primary border-2 text-primary"
                    : "text-secondary"
                }`}
                onClick={() => setActiveTab("query")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  gap: "0.5rem",
                }}
              >
                <Code size={16} />
                Query Builder
              </button>
              <button
                className={`py-2 px-3 d-flex align-items-center ${
                  activeTab === "response"
                    ? "border-bottom border-primary border-2 text-primary"
                    : "text-secondary"
                }`}
                onClick={() => setActiveTab("response")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  gap: "0.5rem",
                }}
              >
                <Server size={16} />
                Response
              </button>
            </div>

            {activeTab === "query" ? (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Query Configuration</h5>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        JSON.stringify(currentQuery?.queryBuilder, null, 2)
                      )
                    }
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                  >
                    <Copy size={14} className="me-1" />
                    Copy Query
                  </button>
                </div>
                <div className="p-3 bg-dark rounded">
                  <pre
                    className="text-light mb-0"
                    style={{ fontSize: "0.875rem" }}
                  >
                    {JSON.stringify(currentQuery?.queryBuilder, null, 2)}
                  </pre>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button
                    onClick={() => executeQuery(currentQuery)}
                    disabled={isExecuting}
                    className="btn btn-success d-flex align-items-center"
                  >
                    {isExecuting ? (
                      <>
                        <Loader2 size={16} className="me-1 animate-spin" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <Play size={16} className="me-1" />
                        Execute Query
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {executionResult ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Query Results</h5>
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-success d-flex align-items-center">
                          {executionResult.status?.toUpperCase() || "SUCCESS"}
                        </span>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              JSON.stringify(executionResult, null, 2)
                            )
                          }
                          className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                        >
                          <Copy size={14} className="me-1" />
                          Copy Results
                        </button>
                      </div>
                    </div>

                    {executionResult.data && (
                      <div className="p-2 bg-light rounded mb-3">
                        <div className="d-flex justify-content-between">
                          <span>
                            <strong>{executionResult.data.length}</strong>{" "}
                            records returned
                          </span>
                          <span>
                            Entity: <strong>{executionResult.entity}</strong>
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="p-3 bg-dark rounded">
                      <pre
                        className="text-light mb-0"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {JSON.stringify(executionResult, null, 2)}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Server size={48} className="text-muted mb-2" />
                    <h5 className="text-muted">No Response Yet</h5>
                    <p className="text-muted">
                      Execute the query to see results here
                    </p>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default SavedApiQueriesList;
